import { useState, type ReactNode } from 'react';
import type { EscopoLoteDto, PreviaLoteResponse } from '@triade/contracts';
import { ChevronDown } from 'lucide-react';
import { useLoteRascunho, usePoliticaPreco } from '@/api/liberacao';
import { usePode } from '@/api/me';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarFracaoPercentual } from '@/lib/format';
import { acaoDoFormulario, aplicarEmPadrao, assinaturaPrevia, FORM_LOTE_INICIAL, ROTULO_REGRA, type FormLote, type RegraArredondamento, type TipoAcaoLote } from './acoes-lote-tipos';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

const OPCOES: Array<{ tipo: TipoAcaoLote; rotulo: string }> = [
  { tipo: 'LUCRO', rotulo: 'Lucro desejado para todas' },
  { tipo: 'MESMO_PRECO', rotulo: 'Mesmo preço para todas' },
  { tipo: 'REAJUSTE', rotulo: 'Reajuste de X%' },
  { tipo: 'ARREDONDAR', rotulo: 'Arredondar' },
  { tipo: 'COPIAR_SUGERIDO', rotulo: 'Copiar o sugerido' },
];

const REGRAS: RegraArredondamento[] = ['CINCO', 'DEZ', 'TERMINAR_90'];
const ROTULO_TODAS = (tipo: TipoAcaoLote) => (tipo === 'MESMO_PRECO' ? 'Todas as condições (valor igual em cada uma)' : 'Todas as condições (calculado em cada uma)');
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

interface PreviaGuardada {
  assinatura: string;
  resultado: PreviaLoteResponse;
}

/**
 * Acoes em lote SEMPRE no rascunho: "Ver prévia" (aplicar:false) mostra o que muda; "Aplicar ao rascunho" (aplicar:true)
 * so vale para a previa da acao atual. Mudou a acao ou o escopo: a previa vira antiga e o aplicar espera nova previa.
 * So para quem tem praticado.gravar e politica TABELA. Nenhum preco e calculado aqui: tudo vem da API.
 */
export function AcoesLote({ escopo, rotuloEscopo, excecoes }: { escopo: EscopoLoteDto; rotuloEscopo: string; excecoes?: ReactNode }) {
  const { pode } = usePode();
  const negociado = usePoliticaPreco().data?.politicaPreco === 'NEGOCIADO';
  const lote = useLoteRascunho();
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState<FormLote>(FORM_LOTE_INICIAL);
  const [previa, setPrevia] = useState<PreviaGuardada | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aplicadoAviso, setAplicadoAviso] = useState(false);
  // "Não liberar mesmo com preço" (S36): so quando quem chama passa a tela de excecoes (Formacao, um item).
  const [modoExcecao, setModoExcecao] = useState(false);

  if (!pode('praticado.gravar') || negociado) return null;

  const pronta = acaoDoFormulario(form);
  const assinatura = pronta.ok ? assinaturaPrevia(escopo, pronta.acao, form.aplicarEm) : null;
  const previaVale = previa !== null && assinatura !== null && previa.assinatura === assinatura;

  function mudar(parcial: Partial<FormLote>) {
    setModoExcecao(false);
    setForm((f) => ({ ...f, ...parcial }));
    setErro(null);
    setAplicadoAviso(false);
  }

  function abrir(v: boolean) {
    if (v) {
      setErro(null);
      setAplicadoAviso(false);
    }
    setAberto(v);
  }

  async function verPrevia() {
    if (!pronta.ok) {
      setErro(pronta.erro);
      return;
    }
    setErro(null);
    setAplicadoAviso(false);
    try {
      const resultado = await lote.mutateAsync({ escopo, acao: pronta.acao, aplicar: false, aplicarEm: form.aplicarEm });
      setPrevia({ assinatura: assinaturaPrevia(escopo, pronta.acao, form.aplicarEm), resultado });
    } catch (e) {
      setPrevia(null);
      setErro(mensagemErro(e));
    }
  }

  async function aplicar() {
    if (!pronta.ok || !previaVale) return;
    setErro(null);
    try {
      await lote.mutateAsync({ escopo, acao: pronta.acao, aplicar: true, aplicarEm: form.aplicarEm });
      setPrevia(null);
      setAplicadoAviso(true);
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  const r = previa?.resultado;

  return (
    <>
      <Button type="button" variant="outline" size="sm" aria-haspopup="dialog" onClick={() => abrir(true)}>
        Ações em lote
        <ChevronDown aria-hidden="true" className="size-4" />
      </Button>
      <Dialog open={aberto} onOpenChange={abrir}>
        <DialogContent className="max-h-[90vh] max-w-[560px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ações em lote</DialogTitle>
            <DialogDescription>
              Aplica no rascunho, nada é publicado. Escopo: {rotuloEscopo}.
            </DialogDescription>
          </DialogHeader>

          <fieldset className="flex flex-col gap-2.5">
            <legend className="mb-2 text-small font-medium">Ação</legend>
            {OPCOES.map((o) => (
              <label key={o.tipo} className="flex cursor-pointer items-center gap-2.5 text-small">
                <input
                  type="radio"
                  name="acao-lote"
                  value={o.tipo}
                  checked={!modoExcecao && form.tipo === o.tipo}
                  onChange={() => mudar({ tipo: o.tipo, aplicarEm: aplicarEmPadrao(o.tipo) })}
                  className="size-4 accent-primary"
                />
                {o.rotulo}
              </label>
            ))}
            {excecoes && (
              <label className="flex cursor-pointer items-center gap-2.5 text-small">
                <input type="radio" name="acao-lote" value="EXCECAO" checked={modoExcecao} onChange={() => setModoExcecao(true)} className="size-4 accent-primary" />
                Não liberar mesmo com preço
              </label>
            )}
          </fieldset>

          {!modoExcecao && (
            <fieldset className="flex flex-col gap-2.5">
              <legend className="mb-2 text-small font-medium">Aplicar em</legend>
              <label className="flex cursor-pointer items-center gap-2.5 text-small">
                <input type="radio" name="aplicar-em" value="PADRAO" checked={form.aplicarEm === 'PADRAO'} onChange={() => mudar({ aplicarEm: 'PADRAO' })} className="size-4 accent-primary" />
                Condição padrão (as outras derivam)
              </label>
              <label className="flex cursor-pointer items-center gap-2.5 text-small">
                <input type="radio" name="aplicar-em" value="TODAS" checked={form.aplicarEm === 'TODAS'} onChange={() => mudar({ aplicarEm: 'TODAS' })} className="size-4 accent-primary" />
                {ROTULO_TODAS(form.tipo)}
              </label>
              <label className="flex cursor-pointer items-center gap-2.5 text-small">
                <input type="radio" name="aplicar-em" value="TODAS_UFS" checked={form.aplicarEm === 'TODAS_UFS'} onChange={() => mudar({ aplicarEm: 'TODAS_UFS' })} className="size-4 accent-primary" />
                Todas as UFs
              </label>
              {form.aplicarEm === 'TODAS_UFS' && (
                <p className="text-caption text-ink-muted">
                  Grava o valor da ação, como exceção manual, em cada UF praticada (tabelas com preço por UF). Tabelas sem preço por UF trazem a mensagem da API.
                </p>
              )}
            </fieldset>
          )}

          {modoExcecao && excecoes}

          <div className={modoExcecao ? 'hidden' : 'flex flex-col gap-3'}>
            {form.tipo === 'LUCRO' && (
              <div>
                <Label htmlFor="lote-lucro" className={ROTULO}>
                  Lucro desejado (%)
                </Label>
                <Input id="lote-lucro" inputMode="decimal" value={form.lucro} onChange={(e) => mudar({ lucro: e.target.value })} placeholder="20" />
                <p className="mt-1 text-caption text-ink-muted">Vale para todas as condições de pagamento de todas as tabelas do escopo; a prévia conta os preços.</p>
              </div>
            )}
            {form.tipo === 'MESMO_PRECO' && (
              <div>
                <Label htmlFor="lote-preco" className={ROTULO}>
                  Preço (R$)
                </Label>
                <Input id="lote-preco" inputMode="decimal" value={form.preco} onChange={(e) => mudar({ preco: e.target.value })} placeholder="0,00" />
              </div>
            )}
            {form.tipo === 'REAJUSTE' && (
              <div>
                <Label htmlFor="lote-reajuste" className={ROTULO}>
                  Reajuste (%, aceita negativo)
                </Label>
                <Input id="lote-reajuste" inputMode="decimal" value={form.reajuste} onChange={(e) => mudar({ reajuste: e.target.value })} placeholder="5" />
              </div>
            )}
            {form.tipo === 'ARREDONDAR' && (
              <div>
                <Label htmlFor="lote-regra" className={ROTULO}>
                  Regra
                </Label>
                <select
                  id="lote-regra"
                  value={form.regra}
                  onChange={(e) => mudar({ regra: e.target.value as RegraArredondamento })}
                  className="h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus"
                >
                  {REGRAS.map((g) => (
                    <option key={g} value={g}>
                      {ROTULO_REGRA[g]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!modoExcecao && erro && (
            <p role="alert" className="text-small text-danger-ink">
              {erro}
            </p>
          )}

          {!modoExcecao && aplicadoAviso && (
            <p role="status" className="rounded-xl border border-success-dot/40 bg-success-bg px-4 py-3 text-small text-success-ink">
              Aplicado ao rascunho (não publicado)
            </p>
          )}

          {!modoExcecao && r && previa && (
            <section aria-label="Prévia do lote" data-testid="previa-lote" className="flex flex-col gap-2 rounded-xl border border-border bg-line-soft px-4 py-3 text-small">
              <p>
                <span className="font-medium">{formatarContagem(r.itens)}</span> {r.itens === 1 ? 'item' : 'itens'} · <span className="font-medium">{formatarContagem(r.precos)}</span>{' '}
                {r.precos === 1 ? 'preço' : 'preços'} serão alterados
              </p>
              <p>
                Ignorados: <span className="font-medium">{formatarContagem(r.ignorados)}</span>
                {r.ignoradosMotivos.length > 0 && ' ('}
                {r.ignoradosMotivos.map((m, i) => (
                  <span key={m.code}>
                    {i > 0 && ', '}
                    {formatarContagem(m.quantidade)} {m.code}
                  </span>
                ))}
                {r.ignoradosMotivos.length > 0 && ')'}
              </p>
              <p>
                Menor lucro: <span className="tabular font-medium">{formatarFracaoPercentual(r.menorLucro)}</span> · Maior lucro:{' '}
                <span className="tabular font-medium">{formatarFracaoPercentual(r.maiorLucro)}</span>
              </p>
              <p data-testid="abaixo-minimo" className={r.abaixoDoMinimo > 0 ? 'font-semibold text-danger-ink' : 'text-ink-secondary'}>
                {formatarContagem(r.abaixoDoMinimo)} {r.abaixoDoMinimo === 1 ? 'fica' : 'ficam'} abaixo do mínimo
              </p>
              <ul data-testid="previa-por-condicao" className="mt-1 flex flex-col gap-1 border-t border-border pt-2 text-caption text-ink-secondary">
                {r.porCondicao.map((c) => (
                  <li key={`${c.tabela}-${c.condicao}`}>
                    <span className="font-medium text-ink">
                      {c.tabela} · {c.condicao}
                    </span>
                    : {formatarContagem(c.precos)} {c.precos === 1 ? 'preço' : 'preços'}, <span className="tabular">{formatarFracaoPercentual(c.menorLucro)}</span>–
                    <span className="tabular">{formatarFracaoPercentual(c.maiorLucro)}</span>, {formatarContagem(c.abaixoDoMinimo)} abaixo do mínimo
                  </li>
                ))}
              </ul>
              {r.porUf && r.porUf.length > 0 && (
                <ul data-testid="previa-por-uf" className="mt-1 flex flex-col gap-1 border-t border-border pt-2 text-caption text-ink-secondary">
                  {r.porUf.map((c) => (
                    <li key={`${c.tabela}-${c.condicao}-${c.uf}`}>
                      <span className="font-medium text-ink">
                        {c.tabela} · {c.condicao} · UF {c.uf}
                      </span>
                      : {formatarContagem(c.precos)} {c.precos === 1 ? 'preço' : 'preços'}, <span className="tabular">{formatarFracaoPercentual(c.menorLucro)}</span>–
                      <span className="tabular">{formatarFracaoPercentual(c.maiorLucro)}</span>, {formatarContagem(c.abaixoDoMinimo)} abaixo do mínimo
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => abrir(false)}>
              Fechar
            </Button>
            {!modoExcecao && (
              <>
                <Button type="button" variant="secondary" onClick={() => void verPrevia()} disabled={lote.isPending}>
                  Ver prévia
                </Button>
                <Button type="button" onClick={() => void aplicar()} disabled={lote.isPending || !previaVale || (previa?.resultado.itens ?? 0) === 0}>
                  Aplicar ao rascunho
                </Button>
              </>
            )}
          </DialogFooter>
          {!modoExcecao && previa && !previaVale && (
            <p className="text-caption text-ink-muted">A ação mudou: refaça a prévia antes de aplicar.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
